import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { deleteIcon, editIcon } from "assets/images";
import CustomPagination from "components/customPagination";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import NoRecords from "components/NoRecords";
import dayjs from "dayjs";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  AddEditGoalsAssessmentPayload,
  AddEditGoalsAssessmentSuccessPayload,
  DeletePersonalAndSmartGoalsPayload,
  DeletePersonalAndSmartGoalsSuccessPayload,
  GetCurrentSLPGoalsPDFPayload,
  GetCurrentSLPGoalsPDFSuccessPayload,
  PersonalAndSmartGoalsBySlpIdPayload,
  PersonalAndSmartGoalsBySlpIdSuccessPayload,
  PersonalAndSmartGoalsPayload,
  PersonalAndSmartGoalsSuccessPayload,
} from "store/slpForm/types";
import {
  DATE_ONLY_FORMAT,
  DATE_VIEW_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_TEN_PAGE_SIZE,
  MAX_INT_VALUE,
  NO_DATA,
} from "utils/constants/constant";
import {
  DELETE_PERSONAL_SMART_GOALS_CONFIRMATION_MSG,
  NO_RECORD_FOUND,
  PERSONAL_SMART_GOALS_DELETED_SUCCESS_MSG,
  PERSONAL_SMART_GOALS_UPDATED_SUCCESS_MSG,
} from "utils/constants/Messages";
import {
  SLPGoalsTableColumns,
  MODAL_TYPES,
  ratingDisplayMap,
  TableCellAlign,
} from "utils/constants/slp";
import {
  hideLoaderForBatchApiCall,
  printPDFHelper,
  showLoader,
  showLoaderForBatchApiCall,
} from "utils/helper";
import {
  IAddEditGoalsAssessment,
  IPersonalSmartGoals,
  IPersonalSmartGoalsListMeta,
} from "utils/interfaces/slp-form";
import AssessGoalModal from "../AssessGoalModal";

type ModalType = typeof MODAL_TYPES[keyof typeof MODAL_TYPES];
type ModalState = { type: ModalType; data: IPersonalSmartGoals } | null;

interface ISLPGoalsTableProps {
  requestHandler: (
    payload: PersonalAndSmartGoalsPayload | PersonalAndSmartGoalsBySlpIdPayload
  ) => void;
  showAssessedColumn?: boolean;
  slpId: number;
  studentId?: number;
  fetchData?: boolean;
  onDataModified?: () => void;
  addEditGoalsAssessmentRequest?: (
    payload: AddEditGoalsAssessmentPayload
  ) => {};
  deletePersonalAndSmartGoalsRequest?: (
    payload: DeletePersonalAndSmartGoalsPayload
  ) => {};
  getCurrentSLPGoalsPDFRequest?: (payload: GetCurrentSLPGoalsPDFPayload) => {};
}

const SLPGoalsTable: React.FC<ISLPGoalsTableProps> = (props) => {
  const {
    requestHandler,
    showAssessedColumn = false,
    slpId,
    studentId,
    fetchData = false,
    onDataModified,
    addEditGoalsAssessmentRequest,
    deletePersonalAndSmartGoalsRequest,
    getCurrentSLPGoalsPDFRequest,
  } = props;

  const [personalAndSmartGoalsData, setPersonalAndSmartGoalsData] = useState<
    IPersonalSmartGoals[]
  >([]);
  const [personalAndSmartGoalsMeta, setPersonalAndSmartGoalsMeta] =
    useState<IPersonalSmartGoalsListMeta>();
  const [activeModal, setActiveModal] = useState<ModalState>(null);
  const [page, setPage] = useState<string>(DEFAULT_TEN_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [assess, setAssess] = useState<boolean>(false);
  const [isOpenDocumentDeleteModal, setIsOpenDocumentDeleteModal] =
    useState<boolean>(false);
  const [exportMenu, setExportMenu] = useState<null | HTMLElement>(null);

  const exportOpen = Boolean(exportMenu);

  const columns = SLPGoalsTableColumns(showAssessedColumn).filter(
    (col) => !col.hidden
  );

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenu(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportMenu(null);
  };

  const handleAssess = (row: IPersonalSmartGoals) => {
    setActiveModal({ type: MODAL_TYPES.ASSESS, data: row });
    setAssess(true);
  };

  const handleAssessClose = () => {
    setAssess(false);
    setActiveModal(null);
  };

  const handleDeleteModal = (row: IPersonalSmartGoals) => {
    setActiveModal({ type: MODAL_TYPES.DELETE, data: row });
    setIsOpenDocumentDeleteModal(true);
  };

  const handleDocumentDeleteCloseModal = () => {
    setIsOpenDocumentDeleteModal(false);
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
  };

  const onPersonalAndSmartGoalsSuccess = (
    response:
      | PersonalAndSmartGoalsSuccessPayload
      | PersonalAndSmartGoalsBySlpIdSuccessPayload
  ) => {
    setPersonalAndSmartGoalsData(response.personalAndSmartGoalsData);
    setPersonalAndSmartGoalsMeta(response.personalAndSmartGoalsPagination);
    hideLoaderForBatchApiCall();
  };

  const getPersonalAndSmartGoals = useCallback(
    (
      pageNum?: number,
      pageSize?: number,
      exportCallback?: (
        data: PersonalAndSmartGoalsBySlpIdSuccessPayload
      ) => void
    ) => {
      if (requestHandler) {
        showLoaderForBatchApiCall();
        const values = {
          pageNo: pageNum ?? Number(pageNo),
          pageSize: pageSize ?? Number(page),
          ...(studentId ? { studentId } : { slpId }),
        };

        if (pageNum) {
          setPageNo(pageNum);
        }
        const payload = {
          values,
          callback: exportCallback ?? onPersonalAndSmartGoalsSuccess,
        };
        requestHandler(payload);
      }
    },
    [requestHandler, pageNo, page, slpId, studentId]
  );

  const onSuccessAddEditGoalsAssessment = (
    response: AddEditGoalsAssessmentSuccessPayload
  ) => {
    if (response.success) {
      onDataModified?.();
      toast.success(PERSONAL_SMART_GOALS_UPDATED_SUCCESS_MSG);
      handleAssessClose();
      hideLoaderForBatchApiCall();
    }
  };

  const onSuccessDeletePersonalAndSmartGoals = (
    response: DeletePersonalAndSmartGoalsSuccessPayload
  ) => {
    if (response.success) {
      onDataModified?.();
      toast.success(PERSONAL_SMART_GOALS_DELETED_SUCCESS_MSG);
      handleDocumentDeleteCloseModal();
      hideLoaderForBatchApiCall();
    }
  };

  const onSubmit = (data: IAddEditGoalsAssessment) => {
    if (addEditGoalsAssessmentRequest) {
      showLoaderForBatchApiCall();
      const payload: AddEditGoalsAssessmentPayload = {
        values: {
          ...data,
          goalAssessedDate: dayjs(data.goalAssessedDate).format(
            DATE_ONLY_FORMAT
          ),
        },
        callback: onSuccessAddEditGoalsAssessment,
      };
      addEditGoalsAssessmentRequest(payload);
    }
  };

  const handleDeleteGoal = (data: { id: number }) => {
    if (deletePersonalAndSmartGoalsRequest) {
      showLoaderForBatchApiCall();
      const payload: DeletePersonalAndSmartGoalsPayload = {
        values: data,
        callback: onSuccessDeletePersonalAndSmartGoals,
      };
      deletePersonalAndSmartGoalsRequest(payload);
    }
  };

  const handleDeleteConfirm = () => {
    if (activeModal?.type === MODAL_TYPES.DELETE) {
      handleDeleteGoal({ id: activeModal.data.id });
    }
    setActiveModal(null);
  };

  useEffect(() => {
    getPersonalAndSmartGoals();
  }, [getPersonalAndSmartGoals, fetchData]);

  const getSLPRequestsModifiedData = (list: Array<IPersonalSmartGoals>) => {
    const data = list?.map((goal: IPersonalSmartGoals) => {
      return {
        "Student Name": goal.studentName,
        "SLP Number": goal.slpNumber,
        "Student Goal": goal.studentGoals,
        "Smart Goal": goal.smartGoals,
        Importance: goal.importance,
        Difficulty: goal.difficulty,
        "Goal Weight": goal.goalWeight,
        "Baseline Level": goal.baselineLevel,
        "Achievement Ratings":
          ratingDisplayMap[goal.achievementRatings] || NO_DATA,
        Variance: goal.variance || NO_DATA,
      };
    });

    return data;
  };

  const goalsExportCallback = (
    data: PersonalAndSmartGoalsBySlpIdSuccessPayload
  ) => {
    if (!data.personalAndSmartGoalsData.length) {
      toast.error(NO_RECORD_FOUND);

      return;
    }

    const modifiedData = getSLPRequestsModifiedData(
      data.personalAndSmartGoalsData
    );
    const csvFileHeaders = [
      "Student Name",
      "SLP Number",
      "Student Goal",
      "Smart Goal",
      "Importance",
      "Difficulty",
      "Goal Weight",
      "Baseline Level",
      "Achievement Ratings",
      "Variance",
    ];

    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers: csvFileHeaders,
      filename: "CurrentSLPGoals",
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(modifiedData);
    hideLoaderForBatchApiCall();
  };

  const handleGoalsExport = () => {
    getPersonalAndSmartGoals(
      DEFAULT_PAGE_NO,
      MAX_INT_VALUE,
      goalsExportCallback
    );
    handleExportClose();
  };

  const currentGoalsPrintCallback = (
    data: GetCurrentSLPGoalsPDFSuccessPayload
  ) => {
    printPDFHelper(data.goalsPDF);
  };

  const handleCurrentGoalsPrint = () => {
    if (getCurrentSLPGoalsPDFRequest) {
      showLoader();
      const value = {
        pageNo: DEFAULT_PAGE_NO,
        pageSize: MAX_INT_VALUE,
        slpId,
        studentId,
      };
      const payload = {
        value,
        callback: currentGoalsPrintCallback,
      };
      getCurrentSLPGoalsPDFRequest(payload);
    }
  };

  return (
    <>
      {showAssessedColumn ? (
        <Typography variant="h4" className="medium-text" sx={{ pb: 2.5 }}>
          SLP Goals
        </Typography>
      ) : (
        <Box className="card-inner-head mb-24">
          <Typography variant="h3" sx={{ fontWeight: 500 }}>
            Goals for this SLP
          </Typography>
          <Box>
            <Button
              id="export-btn"
              aria-controls={exportOpen ? "export-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={exportOpen ? "true" : undefined}
              onClick={handleExportClick}
              disableElevation
              disabled={!personalAndSmartGoalsData.length}
              variant="outlined"
              className="btn-export"
            >
              Export
            </Button>
            <Menu
              id="export-menu"
              anchorEl={exportMenu}
              open={exportOpen}
              onClose={handleExportClose}
              MenuListProps={{
                "aria-labelledby": "export-button",
              }}
              className="export-menu"
            >
              <MenuItem title="Excel" onClick={handleGoalsExport}>
                <Button>Excel</Button>
              </MenuItem>
              <MenuItem title="PDF" onClick={handleCurrentGoalsPrint}>
                <Button>PDF</Button>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}
      <TableContainer>
        <Table className="table-h-md">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={(col.align ?? "left") as TableCellAlign}
                  sx={{
                    width: col.width,
                    minWidth: col.minWidth || col.width,
                    maxWidth: col.width,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {personalAndSmartGoalsData.map((row) => (
              <TableRow key={row.id}>
                {showAssessedColumn && (
                  <TableCell align="center">
                    <div className="table-actions">
                      <IconButton
                        onClick={() => handleAssess(row)}
                        disabled={row.slpId === slpId}
                        className={row.slpId === slpId ? "disabled-icon" : ""}
                      >
                        <img src={editIcon} alt="Edit" />
                      </IconButton>
                    </div>
                  </TableCell>
                )}
                {showAssessedColumn && (
                  <TableCell align="center">{row.slpNumber}</TableCell>
                )}
                <TableCell className="break-word">{row.studentGoals}</TableCell>
                <TableCell className="break-word">{row.smartGoals}</TableCell>
                <TableCell align="center">{row.importance}</TableCell>
                <TableCell align="center">{row.difficulty}</TableCell>
                <TableCell align="center">{row.goalWeight}</TableCell>
                <TableCell className="break-word">
                  {row.baselineLevel}
                </TableCell>
                <TableCell>
                  {row.achievementRatings
                    ? `${ratingDisplayMap[row.achievementRatings]} - ${
                        row.achievementRatings
                      }`
                    : NO_DATA}
                </TableCell>
                <TableCell className="break-word">
                  {row.variance || NO_DATA}
                </TableCell>
                {showAssessedColumn && (
                  <TableCell>
                    {moment(row.slpGoalDate).format(DATE_VIEW_FORMAT)}
                  </TableCell>
                )}
                {showAssessedColumn && (
                  <TableCell>
                    {row.goalAssessedDate
                      ? moment(row.goalAssessedDate).format(DATE_VIEW_FORMAT)
                      : NO_DATA}
                  </TableCell>
                )}

                <TableCell align="center">
                  <div className="table-actions">
                    <IconButton onClick={() => handleDeleteModal(row)}>
                      <img src={deleteIcon} alt="Delete" />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {personalAndSmartGoalsMeta && !!personalAndSmartGoalsData.length ? (
        <CustomPagination
          handleChange={handleChange}
          page={page}
          pageNo={pageNo}
          onPageChange={onPageChange}
          listMeta={personalAndSmartGoalsMeta}
        />
      ) : (
        <NoRecords />
      )}

      <AssessGoalModal
        open={assess}
        onClose={handleAssessClose}
        onSubmit={onSubmit}
        goalData={activeModal?.data || null}
      />

      <DeleteConfirmationModal
        isOpenDeleteConfirmationModal={isOpenDocumentDeleteModal}
        handleDeleteConfirmationModalClose={handleDocumentDeleteCloseModal}
        deleteConfirmationMessage={DELETE_PERSONAL_SMART_GOALS_CONFIRMATION_MSG}
        handleYesClick={handleDeleteConfirm}
      />
    </>
  );
};

export default SLPGoalsTable;
